import React from 'react';
import SEO from '../../components/SEO';
import Breadcrumb from '../../components/Breadcrumb';

const PrivacyPolicy = () => {
  const lastUpdated = 'January 2024';

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="Privacy Policy"
        description="Read Finclify privacy policy to understand how we protect your data and respect your privacy."
        keywords="privacy policy, data protection, privacy, finclify privacy"
        type="website"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'Privacy Policy', url: '/privacy' }
        ]}
      />
      <Breadcrumb
        items={[
          { name: 'Home', url: '/' },
          { name: 'Privacy Policy', url: '/privacy' }
        ]}
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-primary-50 to-blue-100 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Privacy Policy
          </h1>
          <p className="text-lg text-gray-600">
            Last updated: {lastUpdated}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 md:p-12 space-y-8">
            
            <div>
              <p className="text-gray-600 leading-relaxed">
                At Finclify, we are committed to protecting your privacy. This Privacy Policy explains 
                how we collect, use, and safeguard your information when you use our website and services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Information We Collect</h2>
              <div className="space-y-4 text-gray-600">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Information You Provide</h3>
                  <p>
                    When you use our calculators and tools, you may input financial information such as loan amounts, 
                    interest rates, and other financial data. This information is processed locally in your browser 
                    and is not transmitted to our servers.
                  </p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Automatically Collected Information</h3>
                  <p>
                    We may collect certain information automatically when you visit our website, including:
                  </p>
                  <ul className="list-disc list-inside mt-2 space-y-1 ml-4">
                    <li>IP address and browser type</li>
                    <li>Device information and operating system</li>
                    <li>Pages visited and time spent on pages</li>
                    <li>Referring website addresses</li>
                  </ul>
                </div>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. How We Use Your Information</h2>
              <p className="text-gray-600 mb-4">
                We use the information we collect to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Provide and improve our financial calculators and tools</li>
                <li>Analyze website usage to enhance user experience</li>
                <li>Respond to your inquiries and provide customer support</li>
                <li>Ensure website security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Data Storage and Security</h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  <strong className="text-gray-900">Local Processing:</strong> All calculations performed using our 
                  tools are processed locally in your browser. Your financial data never leaves your device unless 
                  you explicitly choose to share it.
                </p>
                <p>
                  <strong className="text-gray-900">No Data Storage:</strong> We do not store your personal financial 
                  information or calculation results on our servers. Each calculation session is independent and temporary.
                </p>
                <p>
                  <strong className="text-gray-900">Security Measures:</strong> We implement appropriate technical and 
                  organizational measures to protect your information against unauthorized access, alteration, disclosure, 
                  or destruction.
                </p>
              </div>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Cookies and Tracking Technologies</h2>
              <p className="text-gray-600 mb-4">
                We may use cookies and similar tracking technologies to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Remember your preferences and settings</li>
                <li>Analyze website traffic and usage patterns</li>
                <li>Improve website functionality</li>
              </ul>
              <p className="text-gray-600 mt-4">
                You can control cookie settings through your browser preferences. However, disabling cookies may 
                affect the functionality of our website.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Third-Party Services</h2>
              <p className="text-gray-600">
                Our website may contain links to third-party websites or services. We are not responsible for the 
                privacy practices of these third parties. We encourage you to review their privacy policies before 
                providing any information.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Your Rights</h2>
              <p className="text-gray-600 mb-4">
                You have the right to:
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-600 ml-4">
                <li>Access information we hold about you</li>
                <li>Request correction of inaccurate information</li>
                <li>Request deletion of your information</li>
                <li>Object to processing of your information</li>
                <li>Request restriction of processing</li>
                <li>Data portability</li>
              </ul>
              <p className="text-gray-600 mt-4">
                To exercise these rights, please contact us using the information provided in our Contact page.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Children's Privacy</h2>
              <p className="text-gray-600">
                Our services are not intended for individuals under the age of 18. We do not knowingly collect 
                personal information from children. If you believe we have collected information from a child, 
                please contact us immediately.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Changes to This Privacy Policy</h2>
              <p className="text-gray-600">
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting 
                the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review 
                this Privacy Policy periodically for any changes.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Contact Us</h2>
              <p className="text-gray-600">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-600">
                  <strong>Email:</strong> privacy@Finclify.com
                </p>
                <p className="text-gray-600 mt-2">
                  <strong>Website:</strong> <a href="/contact" className="text-primary-600 hover:text-primary-700">Contact Page</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;

